import { BarChart3 } from 'lucide-react';
import { useMemo, type FC } from 'react';

interface DailyEntry {
  date: string;
  tokens: number;
}

interface Props {
  dailyUsage: DailyEntry[] | undefined;
}

const UsageDailyChart: FC<Props> = ({ dailyUsage }) => {
  const data = useMemo(() => {
    if (!dailyUsage?.length) return [];
    return dailyUsage.slice(-30);
  }, [dailyUsage]);

  const maxTokens = useMemo(
    () => Math.max(...data.map((d) => d.tokens), 1),
    [data],
  );

  if (!data.length) {
    return (
      <div className="fc-card p-5">
        <CardHeader />
        <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
          Aucune donnée de consommation pour cette période.
        </p>
      </div>
    );
  }

  return (
    <div className="fc-card p-5">
      <CardHeader />
      <div className="mt-4 flex items-end gap-[3px]" style={{ height: 180 }}>
        {data.map((entry) => {
          const pct = (entry.tokens / maxTokens) * 100;
          const day = new Date(entry.date).getDate();
          return (
            <div
              key={entry.date}
              className="group relative flex flex-1 flex-col items-center"
              style={{ height: '100%' }}
            >
              <div className="flex flex-1 w-full items-end">
                <div
                  className="w-full rounded-t bg-blue-500 transition-colors duration-200 group-hover:bg-blue-600 dark:bg-blue-400 dark:group-hover:bg-blue-300"
                  style={{ height: `${Math.max(pct, 2)}%` }}
                />
              </div>
              {day % 5 === 1 && (
                <span className="mt-1 text-[9px] text-slate-400 dark:text-slate-500">
                  {day}
                </span>
              )}
              {/* Tooltip on hover */}
              <div className="pointer-events-none absolute -top-10 left-1/2 z-10 hidden -translate-x-1/2 rounded bg-slate-800 px-2 py-1 text-[10px] text-white shadow group-hover:block dark:bg-slate-600">
                {entry.tokens.toLocaleString('fr-FR')} tokens
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CardHeader: FC = () => (
  <div className="flex items-center gap-2">
    <BarChart3 className="h-4 w-4 text-slate-500 dark:text-slate-400" />
    <h3 className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
      Consommation Journalière (30 jours)
    </h3>
  </div>
);

export default UsageDailyChart;
